/**
 * Dashboard per sport: gli esercizi del profilo prestativo attivo sull'atleta,
 * dominio per dominio, letti nel tempo. E' il profilo a dire che cosa si
 * monitora (D26): non tutto il lavoro registrato, ma le alzate e i benchmark
 * che il modello dello sport considera. Il resto sta negli Andamenti, su
 * richiesta. Legge `GET /coach/monitoring/athletes/{id}/sports?sport=`.
 * Periodo e raggruppamento arrivano da `AthleteTrends`: uno solo per la scheda.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis } from "recharts";
import { fetchAPI } from "../../lib/utils";
import { useChartColors, useChartTooltipStyle } from "../../lib/useChartColors";
import {
	formatMetricValue,
	formatQuantity,
	formatShortDay,
	trendBucketLabel,
} from "../../lib/monitoringUtils";
import { Button } from "../ui/button";

const BUCKET_LABEL_KEYS = {
	week: "monBucketWeek",
	session: "monBucketSession",
	month: "monBucketMonth",
	program: "monBucketProgram",
};

/** La misura che racconta meglio un esercizio di profilo, dalla sua unita'. */
export function defaultMeasureForUnit(unit) {
	switch (unit) {
		case "kg":
			return { field: "top_load_kg", unit: "kg", labelKey: "monMetricTopLoad" };
		case "meters":
			return { field: "meters", unit: "meters", labelKey: "monMetricMeters" };
		case "seconds":
		case "minutes":
		case "minutes_seconds":
			return { field: "seconds", unit: "seconds", labelKey: "monMetricSeconds" };
		case "reps":
		default:
			return { field: "reps", unit: "reps", labelKey: "monMetricReps" };
	}
}

function MiniBars({ data, unit, language }) {
	const colors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	if (!data.some((point) => point.value !== null && point.value !== undefined)) return null;
	return (
		<div className="h-[72px]">
			<ResponsiveContainer width="100%" height="100%">
				<BarChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }} barCategoryGap="35%">
					<XAxis dataKey="label" hide />
					<RechartsTooltip
						contentStyle={tooltipStyle}
						labelStyle={{ color: colors.axis }}
						cursor={{ fill: colors.gridSoft, opacity: 0.4 }}
						formatter={(value) => [formatMetricValue(value, unit, language), ""]}
					/>
					<Bar dataKey="value" fill={colors.volt} maxBarSize={18} radius={[3, 3, 0, 0]} />
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
}

function MovementCard({ exercise, bucket, onOpenTrend, t, language }) {
	const measure = defaultMeasureForUnit(exercise.unit);
	const data = (exercise.buckets || []).map((row) => ({
		label: trendBucketLabel(row, bucket, language),
		value: row[measure.field] ?? null,
	}));
	const totals = exercise.totals || {};
	const facts = [];
	facts.push(`${exercise.sessions} ${t("monMetricSessions").toLowerCase()}`);
	if (exercise.last_date) facts.push(`${t("monWellnessLast")} ${formatShortDay(exercise.last_date, language)}`);
	if (exercise.top_load_kg) facts.push(`${t("monMetricTopLoad").toLowerCase()} ${formatQuantity(exercise.top_load_kg, "kg", language)}`);
	if (totals.reps) facts.push(formatQuantity(totals.reps, "reps", language));
	if (totals.tonnage_kg) facts.push(formatQuantity(totals.tonnage_kg, "kg", language));
	if (totals.meters) facts.push(formatQuantity(totals.meters, "meters", language));
	if (totals.seconds && !totals.meters) facts.push(formatQuantity(totals.seconds, "seconds", language));
	return (
		<article className="rounded-sm border border-border bg-card p-3 space-y-2" data-testid={`sport-card-${exercise.config_id}`}>
			{/* Con due tasti il nome restava di poche lettere: su una card stretta i tasti vanno a capo. */}
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0 flex-1 basis-40">
					<div className="font-medium truncate">{exercise.exercise_name}</div>
					<div className="text-[10px] uppercase tracking-widest text-muted-foreground">
						{t(measure.labelKey)} · {t(BUCKET_LABEL_KEYS[bucket])}
					</div>
				</div>
				<div className="flex items-center gap-1 shrink-0 ml-auto">
					{exercise.family_key ? (
						// Snatch e clean & jerk: l'andamento con tutte le varianti a bilanciere (D27).
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="rounded-sm h-7 px-2 text-xs"
							onClick={() => onOpenTrend(exercise.family_key)}
							aria-label={`${t("monSportOpenFamily")} · ${exercise.exercise_name}`}
						>
							{t("monSportOpenFamily")}
						</Button>
					) : null}
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="rounded-sm h-7 px-2 text-xs"
						onClick={() => onOpenTrend(exercise.movement_key)}
						aria-label={`${t("monSportOpenTrend")} · ${exercise.exercise_name}`}
					>
						{t("monSportOpenTrend")}
						<ArrowUpRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
					</Button>
				</div>
			</div>
			{exercise.exposures ? (
				<>
					<MiniBars data={data} unit={measure.unit} language={language} />
					<p className="text-xs text-muted-foreground">{facts.join(" · ")}</p>
					{exercise.planned ? (
						<p className="text-[11px] text-muted-foreground">
							{t("monPlanned")}: {exercise.planned.sets} {t("monColSets").toLowerCase()} · {formatQuantity(exercise.planned.reps, "reps", language)}
							{" · "}
							{t("monDone")}: {totals.reps ? formatQuantity(totals.reps, "reps", language) : "—"}
						</p>
					) : null}
				</>
			) : (
				<p className="text-xs text-muted-foreground py-3">{t("monSportNoResults")}</p>
			)}
		</article>
	);
}

function BenchmarkCard({ exercise, bucket, onOpenTrend, t, language }) {
	const unit = ["seconds", "minutes", "minutes_seconds"].includes(exercise.unit) ? "seconds" : exercise.unit === "meters" ? "meters" : exercise.unit === "reps" ? "reps" : "count";
	const data = (exercise.buckets || []).map((row) => ({ label: trendBucketLabel(row, bucket, language), value: row.best ?? null }));
	return (
		<article className="rounded-sm border border-border bg-card p-3 space-y-2" data-testid={`sport-card-${exercise.config_id}`}>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="font-medium truncate">{exercise.exercise_name}</div>
					<div className="text-[10px] uppercase tracking-widest text-muted-foreground">
						{t("monSportBest")} · {t(BUCKET_LABEL_KEYS[bucket])}
					</div>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="rounded-sm h-7 px-2 text-xs shrink-0"
					onClick={() => onOpenTrend(exercise.movement_key)}
					aria-label={`${t("monSportOpenTrend")} · ${exercise.exercise_name}`}
				>
					{t("monSportOpenTrend")}
					<ArrowUpRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
				</Button>
			</div>
			{exercise.attempts ? (
				<>
					<MiniBars data={data} unit={unit} language={language} />
					<p className="text-xs text-muted-foreground">
						{exercise.attempts} {t("monSportAttempts")} · {t("monSportBest").toLowerCase()} {formatMetricValue(exercise.best, unit, language)}
						{exercise.last_date ? ` · ${t("monWellnessLast")} ${formatShortDay(exercise.last_date, language)}` : ""}
					</p>
				</>
			) : (
				<p className="text-xs text-muted-foreground py-3">{t("monSportNoResults")}</p>
			)}
		</article>
	);
}

export function SportProfileTrends({
	athleteId,
	sport,
	sportLabel,
	weeks = 12,
	bucket = "week",
	today,
	programId,
	onOpenTrend,
	onOpenAllWork,
	t,
	language,
}) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams({ sport, weeks: String(weeks), bucket, today });
			if (programId && programId !== "all") params.set("program_id", programId);
			setData(await fetchAPI(`/coach/monitoring/athletes/${athleteId}/sports?${params.toString()}`));
		} catch (error) {
			toast.error(error?.message || t("monLoadError"));
		} finally {
			setLoading(false);
		}
	}, [athleteId, sport, weeks, bucket, programId, today, t]);

	useEffect(() => {
		load();
	}, [load]);

	const entry = useMemo(() => (data?.sports || []).find((item) => item.sport === sport) || null, [data, sport]);
	const rangeLabel = data ? `${formatShortDay(data.range.from, language)} – ${formatShortDay(data.range.to, language)}` : "";

	return (
		<div className="space-y-4" data-testid={`sport-trends-${sport}`}>
			<p className="text-xs text-muted-foreground">
				{entry?.model?.name ? `${t("monSportModel")}: ${entry.model.name} · ` : ""}
				{rangeLabel}
				{rangeLabel ? " · " : ""}
				{t("monSportIntro")}
			</p>

			<div className={loading && data ? "opacity-60 transition-opacity space-y-5" : "transition-opacity space-y-5"}>
				{loading && !data ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
						<Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
						{t("loading")}
					</div>
				) : null}
				{entry && entry.exercises_total === 0 ? (
					<div className="rounded-sm border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
						{t("monSportNoExercises")}
					</div>
				) : null}
				{(entry?.domains || [])
					.filter((domain) => domain.exercises.length)
					.map((domain) => (
						<section key={domain.domain_id || "orphans"} className="space-y-2">
							<div className="flex items-center gap-2">
								{domain.color ? (
									<span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: domain.color }} aria-hidden="true" />
								) : null}
								<h3 className="text-xs uppercase tracking-widest text-muted-foreground">
									{domain.name || t("monSportOtherExercises")}
								</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
								{domain.exercises.map((exercise) =>
									exercise.kind === "benchmark" ? (
										<BenchmarkCard key={exercise.config_id} exercise={exercise} bucket={bucket} onOpenTrend={onOpenTrend} t={t} language={language} />
									) : (
										<MovementCard key={exercise.config_id} exercise={exercise} bucket={bucket} onOpenTrend={onOpenTrend} t={t} language={language} />
									),
								)}
							</div>
						</section>
					))}
			</div>
			<p className="text-[11px] text-muted-foreground">
				{t("monSportExtraNote")}{" "}
				{onOpenAllWork ? (
					<button
						type="button"
						onClick={onOpenAllWork}
						className="underline underline-offset-2 hover:text-foreground"
					>
						{t("monTrendsAllWork")}
					</button>
				) : null}
			</p>
		</div>
	);
}
